import { ArrowDownUp, ArrowDown, ArrowUp, EyeOff, Search, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Skeleton } from '../../components/ui/skeleton'
import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { CapabilityProfile, LatestTelemetryView, RawChannelView } from './api-types'
import { CapabilityNotice } from './capability-notice'
import {
  EMPTY_RAW_FILTER,
  filterChannels,
  formatChannelValue,
  sortChannels,
  type RawFilter,
  type RawSortKey,
  type SortDirection,
} from './raw-channels'

/**
 * "Données brutes" — spec §6's transparency requirement, on screen.
 *
 * The claim this panel makes is that RIVENA shows exactly what the FMC150
 * provides, so the interesting rows are the awkward ones: the `io_<avlId>`
 * elements nobody has mapped yet, the channels whose value is null, the readings
 * outside their plausibility range. All three are rendered, and the unmapped ones
 * are labelled as unmapped rather than dropped — an id we have not decoded is the
 * row an installer most needs to see, because it is the difference between "the
 * sensor is not wired" and "the sensor is wired and we do not know its id".
 *
 * Personal-data channels arrive named but unvalued in `withheld`. `telemetry:read_raw`
 * is granted to viewers so they can debug hardware; that is not consent to browse
 * who was driving, and the panel says so instead of pretending the channel is absent.
 */

const SORTABLE: { key: RawSortKey; labelKey: string }[] = [
  { key: 'label', labelKey: 'vehicles:raw.columns.channel' },
  { key: 'value', labelKey: 'vehicles:raw.columns.value' },
  { key: 'category', labelKey: 'vehicles:raw.columns.category' },
  { key: 'ts', labelKey: 'vehicles:raw.columns.timestamp' },
]

const AVAILABILITY_TONE: Record<string, 'success' | 'accent' | 'info' | 'neutral'> = {
  always: 'success',
  can: 'accent',
  optional: 'info',
  derived: 'neutral',
}

export interface RawDataPanelProps {
  latest?: LatestTelemetryView
  capabilities?: CapabilityProfile
  isLoading?: boolean
  className?: string
}

export function RawDataPanel({
  latest,
  capabilities,
  isLoading = false,
  className,
}: RawDataPanelProps) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  const [filter, setFilter] = useState<RawFilter>(EMPTY_RAW_FILTER)
  const [includeDerived, setIncludeDerived] = useState(false)
  const [sortKey, setSortKey] = useState<RawSortKey>('label')
  const [direction, setDirection] = useState<SortDirection>('asc')

  const source: RawChannelView[] = useMemo(() => {
    if (latest === undefined) return []
    return includeDerived ? [...latest.channels, ...latest.derived] : latest.channels
  }, [latest, includeDerived])

  // Built from the rows actually present, never from the registry's full
  // vocabulary: offering a category filter that can only ever return nothing is a
  // dead end the reader has to discover by trying it.
  const categories = useMemo(
    () =>
      [...new Set(source.flatMap((row) => (row.category === null ? [] : [row.category])))].sort(),
    [source],
  )
  const availabilities = useMemo(
    () =>
      [
        ...new Set(source.flatMap((row) => (row.availability === null ? [] : [row.availability]))),
      ].sort(),
    [source],
  )

  const rows = useMemo(
    () => sortChannels(filterChannels(source, filter), sortKey, direction, formatters.locale),
    [source, filter, sortKey, direction, formatters.locale],
  )

  function toggleSort(key: RawSortKey) {
    if (key === sortKey) {
      setDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    // A new column starts ascending except for time, where "most recent first" is
    // what anyone opening a raw panel is actually looking for.
    setDirection(key === 'ts' ? 'desc' : 'asc')
  }

  const unmapped = source.filter((row) => !row.known).length

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader
        title={t('vehicles:raw.title')}
        description={
          latest?.observedAt
            ? t('vehicles:raw.observedAt', { when: formatters.dateTime(latest.observedAt) })
            : t('vehicles:raw.subtitle')
        }
      />

      <CardContent className="space-y-4">
        {capabilities !== undefined && capabilities.reasonCode != null ? (
          <CapabilityNotice profile={capabilities} />
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <Input
              label={t('vehicles:raw.filter.search')}
              value={filter.query}
              placeholder={t('vehicles:raw.filter.searchPlaceholder')}
              onChange={(event) => setFilter((f) => ({ ...f, query: event.target.value }))}
              trailing={<Search aria-hidden className="me-2 h-4 w-4 text-foreground-subtle" />}
            />
          </div>

          <Select
            label={t('vehicles:raw.columns.category')}
            value={filter.category}
            onChange={(value) => setFilter((f) => ({ ...f, category: value }))}
            options={[
              { value: 'all', label: t('vehicles:raw.filter.allCategories') },
              ...categories.map((c) => ({ value: c, label: t(`vehicles:raw.category.${c}`) })),
            ]}
          />

          <Select
            label={t('vehicles:raw.columns.availability')}
            value={filter.availability}
            onChange={(value) => setFilter((f) => ({ ...f, availability: value }))}
            options={[
              { value: 'all', label: t('vehicles:raw.filter.allAvailabilities') },
              ...availabilities.map((a) => ({
                value: a,
                label: t(`vehicles:raw.availability.${a}.label`),
              })),
            ]}
          />

          <Button
            type="button"
            size="md"
            variant={filter.unmappedOnly ? 'primary' : 'secondary'}
            aria-pressed={filter.unmappedOnly}
            onClick={() => setFilter((f) => ({ ...f, unmappedOnly: !f.unmappedOnly }))}
          >
            {t('vehicles:raw.filter.unmappedOnly', { unmapped })}
          </Button>

          <Button
            type="button"
            size="md"
            variant={includeDerived ? 'primary' : 'secondary'}
            aria-pressed={includeDerived}
            onClick={() => setIncludeDerived((value) => !value)}
          >
            {t('vehicles:raw.filter.includeDerived')}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyTable hasSource={source.length > 0} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[46rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  {SORTABLE.map(({ key, labelKey }) => (
                    <th
                      key={key}
                      scope="col"
                      aria-sort={
                        sortKey === key
                          ? direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      className="px-2 py-2 text-start text-xs font-medium uppercase tracking-wide text-foreground-subtle"
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(key)}
                        className="inline-flex items-center gap-1 rounded hover:text-foreground"
                      >
                        {t(labelKey)}
                        <SortIcon active={sortKey === key} direction={direction} />
                      </button>
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="px-2 py-2 text-start text-xs font-medium uppercase tracking-wide text-foreground-subtle"
                  >
                    {t('vehicles:raw.columns.availability')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ChannelRow key={row.channel} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-foreground-subtle">
          {t('vehicles:raw.summary', {
            shown: rows.length,
            total: source.length,
            unmapped,
          })}
          {latest !== undefined
            ? ` · ${t('vehicles:raw.lookback', { hours: latest.lookbackHours })}`
            : ''}
        </p>

        {latest !== undefined && latest.withheld.length > 0 ? (
          <WithheldList latest={latest} />
        ) : null}
      </CardContent>
    </Card>
  )
}

function ChannelRow({ row }: { row: RawChannelView }) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  return (
    <tr className="border-b border-border last:border-0 hover:bg-surface-hover">
      <td className="px-2 py-2 align-top">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-foreground">{row.label}</span>
          <span className="font-mono text-xs text-foreground-subtle">{row.channel}</span>
          {!row.known ? (
            <span>
              <Badge tone="warning">
                {row.avlId === null
                  ? t('vehicles:raw.unmapped')
                  : t('vehicles:raw.unmappedAvl', { id: row.avlId })}
              </Badge>
            </span>
          ) : null}
        </div>
      </td>

      <td className="px-2 py-2 align-top">
        <div className="flex flex-col gap-0.5">
          <span className="tabular-nums text-foreground">
            {formatChannelValue(row, { formatters, t })}
          </span>
          {row.outOfRange ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-danger">
              <TriangleAlert aria-hidden className="h-3 w-3" />
              {t('vehicles:raw.outOfRange')}
            </span>
          ) : null}
        </div>
      </td>

      <td className="px-2 py-2 align-top">
        {row.category === null ? (
          <span className="text-xs text-foreground-subtle">{t('vehicles:raw.noCategory')}</span>
        ) : (
          <Badge tone="neutral">{t(`vehicles:raw.category.${row.category}`)}</Badge>
        )}
      </td>

      <td className="whitespace-nowrap px-2 py-2 align-top text-xs tabular-nums text-foreground-muted">
        {formatters.dateTime(row.ts)}
      </td>

      <td className="px-2 py-2 align-top">
        {row.availability === null ? (
          <span className="text-xs text-foreground-subtle">
            {t('vehicles:raw.unknownAvailability')}
          </span>
        ) : (
          <span title={t(`vehicles:raw.availability.${row.availability}.hint`)}>
            <Badge tone={AVAILABILITY_TONE[row.availability] ?? 'neutral'}>
              {t(`vehicles:raw.availability.${row.availability}.label`)}
            </Badge>
          </span>
        )}
      </td>
    </tr>
  )
}

/** Named, timestamped, unvalued. Absence with a reason beats a channel that vanishes. */
function WithheldList({ latest }: { latest: LatestTelemetryView }) {
  const { t } = useTranslation()

  return (
    <div className="rounded-lg border border-border bg-surface-muted/50 p-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <EyeOff aria-hidden className="h-4 w-4" />
        {t('vehicles:raw.withheld.title', { withheld: latest.withheld.length })}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-foreground-muted">
        {t('vehicles:raw.withheld.description')}
      </p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {latest.withheld.map((row) => (
          <li key={row.channel}>
            <Badge tone="neutral">{row.label}</Badge>
          </li>
        ))}
      </ul>
    </div>
  )
}

function EmptyTable({ hasSource }: { hasSource: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface/60 px-4 py-8 text-center">
      <p className="text-sm font-semibold text-foreground">
        {t(hasSource ? 'vehicles:raw.noMatch.title' : 'vehicles:raw.noChannels.title')}
      </p>
      <p className="mx-auto mt-1 max-w-prose text-sm leading-relaxed text-foreground-muted">
        {t(hasSource ? 'vehicles:raw.noMatch.description' : 'vehicles:raw.noChannels.description')}
      </p>
    </div>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDirection }) {
  if (!active) return <ArrowDownUp aria-hidden className="h-3 w-3 opacity-50" />
  return direction === 'asc' ? (
    <ArrowUp aria-hidden className="h-3 w-3" />
  ) : (
    <ArrowDown aria-hidden className="h-3 w-3" />
  )
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-foreground hover:border-border-strong"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}
